import { AlertTriangle, ArrowRight, Undo2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Shown when the user navigates away from the Report Builder and their latest
 * changes could NOT be captured (MDRS-555 / audit RB-9).
 *
 * Deliberately rare. The guard saves silently on the way out whenever it can —
 * a dialog in front of a save that is about to succeed is pure interruption.
 * This only appears when the document genuinely could not be serialised, which
 * is the one case where continuing really does lose the work.
 *
 * Like `DraftConflictModal` it can't be dismissed by Escape or a click
 * outside: both of those would read as "cancel", but the pending navigation
 * has to resolve one way or the other, and leaving it unresolved would strand
 * the router mid-transition. Every exit here is an explicit choice.
 */
type Props = {
  /** False = no blocked navigation; renders nothing. */
  open: boolean;
  /** Abandon the unsaved changes and continue to the new page. */
  onLeave: () => void;
  /** Stay on the report so the work can be saved or copied out. */
  onStay: () => void;
};

export const LeaveWithoutSavingModal = ({ open, onLeave, onStay }: Props) => {
  if (!open) return null;
  return (
    <Dialog open>
      <DialogContent
        className="rounded-2xl bg-background px-8 py-10"
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="flex flex-col items-center gap-6 text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="size-7 text-amber-600" aria-hidden />
          </div>
          <DialogTitle>Your latest changes haven&apos;t been saved</DialogTitle>
          <DialogDescription className="space-y-4 text-left">
            <span className="block">
              We couldn&apos;t save the changes you just made to this report, so
              leaving now would lose them.
            </span>
            <span className="block">
              Stay on the page and we&apos;ll keep trying — or leave and discard
              them.
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Staying is the safe choice, so it's the primary action. */}
        <div className="mt-2 flex flex-col gap-3">
          <Button onClick={onStay}>
            <Undo2 className="mr-2 size-4" aria-hidden />
            Stay on this report
          </Button>
          <Button variant="simple-outline" onClick={onLeave}>
            <ArrowRight className="mr-2 size-4" aria-hidden />
            Leave without saving
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
